/** Typed calls for the Reports screens. Shapes mirror apps/api/src/report-routes.ts. */

import { apiCall, orgPath } from '../api.ts';

export type WidgetType =
  | 'METRIC'
  | 'LINE'
  | 'BAR'
  | 'STACKED_BAR'
  | 'DONUT'
  | 'TABLE'
  | 'TEXT'
  | 'HEADER'
  | 'GOAL'
  | 'IMAGE'
  | 'AREA'
  | 'HORIZONTAL_BAR'
  | 'PIE'
  | 'GAUGE'
  | 'SPARKLINE'
  | 'TOC'
  | 'EMBED'
  | 'AI_SUMMARY';
export type MetricFormat = 'number' | 'money' | 'percent' | 'duration' | 'rating';

export interface CatalogueMetric {
  key: string;
  label: string;
  format: MetricFormat;
  higher_is_better: boolean;
  /** Live sources (GA4): the metric picker group, and whether it is on by default. */
  group?: string;
  is_default?: boolean;
}

export interface CatalogueSource {
  key: string;
  label: string;
  category: string;
  needs_attachment: boolean;
  /** Answered live by the provider (GA4) rather than from stored rows. */
  live?: boolean;
  metrics: CatalogueMetric[];
  dimensions: { key: string; label: string }[];
}

export interface Catalogue {
  widget_types: WidgetType[];
  data_widget_types: WidgetType[];
  date_presets: string[];
  compare_modes: string[];
  sources: CatalogueSource[];
}

export interface Filter {
  dimension: string;
  op: 'is' | 'is_not' | 'contains';
  values: string[];
}

export interface WidgetData {
  source?: string;
  metrics?: string[];
  dimension?: string | null;
  filters?: Filter[];
  sort?: { metric: string; direction: 'asc' | 'desc' };
  limit?: number;
  range?: RangeSetting | null;
  /** GOAL widgets: the target value, in the metric's own unit (cents for money). */
  target?: number;
  /** Roll-ups: read this one client instead of adding the clients together. */
  client_id?: string | null;
}

export interface WidgetDisplay {
  title?: string;
  text?: string;
  /** AI Summary: what the agency asks SynAssistant to focus on. */
  guidance?: string;
  show_comparison?: boolean;
  show_totals?: boolean;
  show_sparkline?: boolean;
  tone?: number;
  background?: string;
  /** EMBED: the https address shown in a sandboxed frame. */
  url?: string;
  /** IMAGE: the picture as a PNG, JPEG or WebP data URL, its alt text and how it fills the box. */
  image?: string;
  alt?: string;
  fit?: 'contain' | 'cover';
  /** HEADER (title): fixed text or a fact about the report (report-facts.ts), its style and alignment. */
  title_source?: string;
  heading?: 'h1' | 'h2' | 'h3';
  align?: 'left' | 'center' | 'right';
}

export interface Widget {
  id?: string;
  type: WidgetType;
  x: number;
  y: number;
  w: number;
  h: number;
  data: WidgetData;
  display: WidgetDisplay;
}

export interface Page {
  id?: string;
  title: string;
  settings: Record<string, unknown>;
  widgets: Widget[];
}

export interface RangeSetting {
  preset: string;
  n?: number;
  from?: string;
  to?: string;
  includeToday?: boolean;
}

export interface CompareSetting {
  mode: string;
  from?: string;
  to?: string;
  /** Show changes as a percentage (default) or in the metric's own unit. */
  format?: 'PERCENT' | 'ABSOLUTE';
}

export interface DocumentSettings {
  range?: RangeSetting;
  compare?: CompareSetting;
  timezone?: string;
  mode?: 'LIVE' | 'SAMPLE';
  /** Close gaps when a widget moves (Page setup). On unless switched off. */
  auto_align?: boolean;
  /** Roll-ups: the clients it covers (at most 50). */
  clients?: string[];
}

export interface ReportDocument {
  id: string;
  business_unit_id: string | null;
  kind: DocumentKind;
  scope: string;
  name: string;
  settings: DocumentSettings;
  theme: Record<string, unknown>;
  revision: number;
  updated_at: string;
  /** Set while the document is visible in its client's portal. */
  shared_at?: string | null;
  /** Set on a share link's frozen copy: the document it was copied from. */
  share_of?: string | null;
}

export interface RemovedDocument {
  id: string;
  business_unit_id: string | null;
  kind: DocumentKind;
  name: string;
  deleted_at: string;
}

export type DocumentKind = 'DASHBOARD' | 'SLIDE_DECK' | 'DOCUMENT';

export interface Template {
  key: string;
  name: string;
  description: string;
  kinds: DocumentKind[];
  pages: Page[];
}

export interface Client {
  id: string;
  name: string;
  website: string | null;
  timezone: string | null;
  created_at: string;
}

export interface Change {
  absolute: number;
  percent: number | null;
}

export interface WidgetResult {
  id: string;
  state: 'OK' | 'NOT_CONNECTED' | 'EMPTY' | 'ERROR';
  /** Why a live source failed (ERROR only). */
  error?: string;
  range: { from: string; to: string };
  compare_range: { from: string; to: string } | null;
  granularity: 'day' | 'week' | 'month';
  metrics: { key: string; value: number | null; previous: number | null; change: Change | null }[];
  series: { bucket: string; values: Record<string, number | null> }[];
  compare_series: { bucket: string; values: Record<string, number | null> }[] | null;
  rows:
    | {
        label: string;
        values: Record<string, number | null>;
        compare: Record<string, number | null> | null;
      }[]
    | null;
  total_rows: number | null;
}

export interface Attachment {
  id: string;
  source: string;
  source_ref: string;
  label: string | null;
}

const base = (organisationId: string, path: string) => orgPath(organisationId, `/reports${path}`);

export const reportsApi = {
  catalogue: (org: string) =>
    apiCall<{ catalogue: Catalogue; templates: Template[] }>(base(org, '/catalogue')),
  clients: (org: string) =>
    apiCall<{ clients: Client[] }>(base(org, '/clients')).then((data) => data.clients),
  documents: (org: string, businessUnitId: string) =>
    apiCall<{ documents: ReportDocument[] }>(
      base(org, `/documents?business_unit_id=${encodeURIComponent(businessUnitId)}`),
    ).then((data) => data.documents),
  create: (
    org: string,
    input: {
      business_unit_id: string | null;
      kind: DocumentKind;
      name: string;
      template?: string;
      clone_from?: string;
      settings?: DocumentSettings;
    },
  ) =>
    apiCall<{ document: ReportDocument; pages: Page[] }>(base(org, '/documents'), {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  get: (org: string, id: string) =>
    apiCall<{ document: ReportDocument; pages: Page[] }>(base(org, `/documents/${id}`)),
  save: (
    org: string,
    id: string,
    input: {
      revision: number;
      name: string;
      settings: DocumentSettings;
      theme?: Record<string, unknown>;
      pages: Page[];
      note?: string;
    },
  ) =>
    apiCall<{ document: ReportDocument; pages: Page[] }>(base(org, `/documents/${id}`), {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  removed: (org: string, clientId: string) =>
    apiCall<{ documents: RemovedDocument[] }>(
      base(org, `/removed?business_unit_id=${encodeURIComponent(clientId)}`),
    ).then((data) => data.documents),
  restoreRemoved: (org: string, id: string) =>
    apiCall<{ restored: boolean }>(base(org, `/removed/${id}/restore`), { method: 'POST' }),
  remove: (org: string, id: string) =>
    apiCall<{ deleted: boolean }>(base(org, `/documents/${id}`), { method: 'DELETE' }),
  versions: (org: string, id: string) =>
    apiCall<{ versions: { revision: number; note: string | null; created_at: string }[] }>(
      base(org, `/documents/${id}/versions`),
    ).then((data) => data.versions),
  restore: (org: string, id: string, revision: number, baseRevision: number) =>
    apiCall<{ document: ReportDocument; pages: Page[] }>(base(org, `/documents/${id}/restore`), {
      method: 'POST',
      body: JSON.stringify({ revision, base_revision: baseRevision }),
    }),
  query: (
    org: string,
    input: {
      business_unit_id: string | null;
      /** Roll-ups: the clients to add together. */
      client_ids?: string[];
      /** The view editor: an unsaved view, asked for as source "view:preview". */
      preview_view?: unknown;
      mode: 'LIVE' | 'SAMPLE';
      timezone?: string;
      range: RangeSetting;
      compare?: CompareSetting;
      widgets: object[];
    },
  ) =>
    apiCall<{ results: WidgetResult[]; today: string; range: { from: string; to: string } }>(
      base(org, '/query'),
      { method: 'POST', body: JSON.stringify(input) },
    ),
  sources: (org: string, businessUnitId: string) =>
    apiCall<{ sources: Attachment[] }>(base(org, `/clients/${businessUnitId}/sources`)).then(
      (data) => data.sources,
    ),
  attachable: (org: string, source: string) =>
    apiCall<{ items: { id: string; label: string }[] }>(
      base(org, `/attachable?source=${encodeURIComponent(source)}`),
    ).then((data) => data.items),
  attach: (org: string, businessUnitId: string, source: string, sourceRef: string) =>
    apiCall<{ source: Attachment }>(base(org, `/clients/${businessUnitId}/sources`), {
      method: 'POST',
      body: JSON.stringify({ source, source_ref: sourceRef }),
    }),
  detach: (org: string, businessUnitId: string, attachmentId: string) =>
    apiCall<{ deleted: boolean }>(base(org, `/clients/${businessUnitId}/sources/${attachmentId}`), {
      method: 'DELETE',
    }),
};
